import { Bolt } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Toolbar,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import React, { memo, useState } from "react";
import { useDispatch } from "react-redux";
import { CustomTablePagination } from "../../../components/table/tableAction";
import { getComparator, tableSort } from "../../../components/table/tableUtils";
import { importStateFile } from "./../../../redux/actions/stateFileAction";
import { useStyles } from "./styles";

const columns = [
  { field: "name", label: "Service" },
  { field: "total", label: "Total" },
  { field: "terraform_count", label: "Terraform Count" },
  { field: "non_terraform_count", label: "Non Terraform Count" },
];

const TableToolbar = ({ state, selected = [], onSuccess }) => {
  const dispatch = useDispatch();

  const handleRunCode = () => {
    let services = selected.map((service) => service.split("-")[0]);
    const selectedServices = services.join(",");
    const payload = {
      services: selectedServices,
      cred_name: state?.credential?.credName,
      region: state?.region?.region,
      s3_url: state?.url,
      file: state.file,
      file_exists: state.fileType === "file",
    };
    dispatch(
      importStateFile(payload, () => {
        onSuccess(services);
      })
    );
  };

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(selected.length > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity
            ),
        }),
      }}
    >
      <Box width="100%" display="flex" justifyContent="space-between">
        <Typography color="inherit" variant="h6">
          {selected.length > 0
            ? `${selected.length}  Service Selected`
            : "Service Code"}
        </Typography>
        <Button
          onClick={handleRunCode}
          disabled={!selected.length > 0}
          startIcon={<Bolt />}
          variant="contained"
        >
          Run Code
        </Button>
      </Box>
    </Toolbar>
  );
};

const EnhancedTableHead = (props) => {
  const { order, orderBy, onRequestSort } = props;
  const classes = useStyles();

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead className={classes.headerCell}>
      <TableRow>
        <TableCell padding="checkbox">
        </TableCell>
        {columns.map((headCell, index) => (
          <TableCell
            align={index === 0 ? "left" : "center"}
            key={headCell.field}
            sortDirection={orderBy === headCell.field ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.field}
              direction={orderBy === headCell.field ? order : "asc"}
              onClick={createSortHandler(headCell.field)}
            >
              {headCell.label}
              {orderBy === headCell.field ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const EnhancedTable = ({ state, data, onSuccess }) => {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("calories");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleClick = (field) => {
    const selectedIndex = selected.indexOf(field);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, field);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(0);
  };

  const isSelected = (field) => selected.indexOf(field) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      tableSort(data, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [order, data, orderBy, page, rowsPerPage]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Paper
        sx={{
          borderRadius: "12px",
          width: "100%",
          overflow: "hidden",
          boxShadow: "0px 1px 2px 0px rgba(0,0,0,0.12)",
        }}
      >
        <TableToolbar state={state} selected={selected} onSuccess={onSuccess} />
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={data.length}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const { migration_support, migration_values } = row;
                const isItemSelected = isSelected(
                  `${migration_values}-${index}`
                );
                const labelId = `enhanced-table-checkbox-${index}`;
                return (
                  <TableRow
                    hover
                    onClick={(event) =>
                      migration_support &&
                      handleClick(`${migration_values}-${index}`)
                    }
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.field}
                    selected={isItemSelected}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        disabled={!migration_support}
                        checked={migration_support ? isItemSelected : false}
                        inputProps={{
                          "aria-labelledby": labelId,
                        }}
                      />
                    </TableCell>
                    {columns.map((column, index) => {
                      const value = column.render
                        ? column.render(row)
                        : row[column.field];
                      return (
                        <TableCell
                          key={`${column.field}-row-data`}
                          align={
                            index === 0 ? "left" : column?.align || "center"
                          }
                        >
                          {value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination
          count={data.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(val) => setPage(val)}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default memo(EnhancedTable);
